import { cn } from '@/utilities/ui'
import Link from 'next/link'
import React, { Fragment } from 'react'

import type { Post, Media as MediaType, Category } from '@payload-types'

import { Media } from '@/components/media'
import { Badge } from '@/components/ui/badge'

export type CardPostData = Pick<
  Post,
  | 'id'
  | 'displayId'
  | 'slug'
  | 'categories'
  | 'meta'
  | 'title'
  | 'heroImage'
  | 'createdAt'
  | 'publishedAt'
  | 'spot'
>

export type CardVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'hero'
  | 'feature'
  | 'category'
  | 'image-only'

export interface CardProps {
  alignItems?: 'center'
  className?: string
  doc?: CardPostData
  showCategories?: boolean
  showDate?: boolean
  title?: string
  variant?: CardVariant
  href?: string
  priority?: boolean
  imgClassName?: string
}

export const Card: React.FC<CardProps> = (props) => {
  const {
    className,
    doc,
    showCategories,
    showDate = true,
    title: titleFromProps,
    variant = 'default',
    href: customHref,
    priority = false,
    imgClassName,
  } = props

  const { displayId, slug, categories, meta, title, heroImage, spot } = doc || {}
  const { description, image: metaImage } = meta || {}

  const imageToDisplay = heroImage || metaImage

  const hasCategories = categories && Array.isArray(categories) && categories.length > 0
  const firstCategory = hasCategories ? categories[0] : null
  const titleToUse = titleFromProps || title
  const sanitizedDescription = description?.replace(/\s/g, ' ')
  const href =
    customHref || (slug && displayId ? `/${slug}/${displayId}` : slug ? `/posts/${slug}` : '#')

  if (variant === 'image-only') {
    return (
      <div
        className={cn(
          'overflow-hidden relative border-none shadow-none rounded-none group h-full',
          className,
        )}
      >
        {imageToDisplay && typeof imageToDisplay === 'object' && 'url' in imageToDisplay && (
          <Link href={href} className="block absolute inset-0 z-0 aspect-video">
            <Media
              resource={imageToDisplay as MediaType}
              priority={priority}
              className="object-cover w-full h-full"
              imgClassName="object-cover w-full h-full rounded-t-lg"
              fill
            />
          </Link>
        )}

        {/* Category Badges */}
        {showCategories && hasCategories && firstCategory && (
          <div className="absolute top-4 right-4 md:top-6 md:right-6 z-10 flex flex-wrap gap-2">
            {typeof firstCategory === 'object' &&
              firstCategory !== null &&
              'slug' in firstCategory &&
              firstCategory.slug && (
                <Badge
                  variant="secondary"
                  className="bg-white/95 text-gray-800 hover:bg-white shadow rounded-full px-3 py-1 text-xs font-semibold flex items-center transition-all duration-200"
                >
                  <Link href={`/haberler/${firstCategory.slug}`}>{firstCategory.title}</Link>
                </Badge>
              )}
          </div>
        )}
      </div>
    )
  }

  // Hero variant (for main carousel slide)
  if (variant === 'hero') {
    return (
      <div
        className={cn(
          'overflow-hidden relative border-none shadow-none rounded-none group h-full',
          className,
        )}
      >
        {imageToDisplay && typeof imageToDisplay === 'object' && 'url' in imageToDisplay && (
          <Link href={href} className="block absolute inset-0 z-0 aspect-video">
            <Media
              resource={imageToDisplay as MediaType}
              priority={priority}
              className="object-cover w-full h-full"
              imgClassName="object-cover w-full h-full rounded-t-lg"
              fill
            />
            {/* Overlay for text contrast */}
            <div
              className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent transition-opacity duration-300"
              aria-hidden="true"
            />
          </Link>
        )}

        {/* Category Badges */}
        {showCategories && hasCategories && firstCategory && (
          <div className="absolute top-4 right-4 md:top-6 md:right-6 z-10 flex flex-wrap gap-2">
            {typeof firstCategory === 'object' &&
              firstCategory !== null &&
              'slug' in firstCategory &&
              firstCategory.slug && (
                <Badge
                  variant="secondary"
                  className="bg-white/95 text-gray-800 hover:bg-white shadow rounded-full px-3 py-1 text-xs font-semibold flex items-center transition-all duration-200"
                >
                  <Link href={`/haberler/${firstCategory.slug}`}>{firstCategory.title}</Link>
                </Badge>
              )}
          </div>
        )}

        {/* Post Title */}
        {titleToUse && (
          <div className="absolute bottom-9 left-0 right-0 px-4 md:px-6 pb-4 z-10">
            <Link href={href}>
              <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-white leading-tight line-clamp-2">
                {titleToUse}
              </h2>
            </Link>
          </div>
        )}
      </div>
    )
  }

  // Primary variant (used for sidebar primary post)
  if (variant === 'primary') {
    return (
      <Link href={href} className="block group h-full">
        <article
          className={cn(
            'overflow-hidden rounded-lg border-none bg-card shadow-none hover:shadow-md group-hover:scale-102 transition-all duration-300 h-full flex flex-col',
            className,
          )}
        >
          {imageToDisplay && typeof imageToDisplay === 'object' && 'url' in imageToDisplay && (
            <div className="overflow-hidden flex-shrink-0">
              <Media
                resource={imageToDisplay as MediaType}
                priority={priority}
                className={cn('w-full h-full aspect-video', className)}
                imgClassName={cn('object-cover w-full h-full aspect-video', imgClassName)}
              />
            </div>
          )}
          <div className="px-4 py-5 flex-grow flex flex-col">
            {showCategories &&
              hasCategories &&
              firstCategory &&
              typeof firstCategory === 'object' && (
                <div className="text-xs text-muted-foreground mb-2">{firstCategory.title}</div>
              )}
            <h2 className="font-medium text-base leading-snug line-clamp-2 group-hover:text-primary text-slate-900 transition-colors duration-200">
              {titleToUse}
            </h2>
            {doc && showDate && (
              <p className="text-xs text-slate-500 mt-auto pt-3">
                {new Date(
                  (doc.publishedAt as string) ||
                    (doc.createdAt as string) ||
                    new Date().toISOString(),
                ).toLocaleDateString('tr-TR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            )}
          </div>
        </article>
      </Link>
    )
  }
  // Category variant (used for category blocks)
  if (variant === 'category') {
    return (
      <Link href={href} className="block group mb-4">
        <article
          className={cn(
            'overflow-hidden rounded-lg border-none bg-card shadow-none hover:shadow-md group-hover:scale-102 transition-all duration-300',
            className,
          )}
        >
          {imageToDisplay && typeof imageToDisplay === 'object' && 'url' in imageToDisplay && (
            <div className="overflow-hidden">
              <Media
                resource={imageToDisplay as MediaType}
                priority={priority}
                className={cn('w-full h-full aspect-video', className)}
                imgClassName={cn('object-cover w-full h-full aspect-video', imgClassName)}
              />
            </div>
          )}
          <div className="px-4 py-5 flex flex-col">
            {showCategories &&
              hasCategories &&
              firstCategory &&
              typeof firstCategory === 'object' && (
                <div className="text-xs text-muted-foreground mb-2">{firstCategory.title}</div>
              )}
            <h2 className="font-semibold text-sm md:text-base leading-snug line-clamp-2 group-hover:text-primary text-card-foreground bg-stale/900 transition-colors duration-200">
              {titleToUse}
            </h2>
            {doc && showDate && (
              <p className="text-xs text-slate-500 mt-auto pt-3">
                {new Date(
                  (doc.publishedAt as string) ||
                    (doc.createdAt as string) ||
                    new Date().toISOString(),
                ).toLocaleDateString('tr-TR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            )}
          </div>
        </article>
      </Link>
    )
  }
  // Secondary variant (used for sidebar secondary posts)
  if (variant === 'secondary') {
    return (
      <Link href={href} className="block group">
        <article
          className={cn(
            'overflow-hidden border-none shadow-none bg-background rounded-lg flex gap-3 items-center p-0 flex-row justify-center hover:shadow-md group-hover:scale-102 transition-all duration-300',
            className,
          )}
        >
          {imageToDisplay && typeof imageToDisplay === 'object' && 'url' in imageToDisplay && (
            <div className="w-32 flex-shrink-0 flex items-center justify-center overflow-hidden rounded-md">
              <Media
                resource={imageToDisplay as MediaType}
                priority={priority}
                className={cn('w-full h-full aspect-video', className)}
                imgClassName={cn(
                  'object-cover w-full h-full aspect-video rounded-md',
                  imgClassName,
                )}
              />
            </div>
          )}
          <div className="flex-grow">
            {showCategories &&
              hasCategories &&
              firstCategory &&
              typeof firstCategory === 'object' && (
                <div className="text-xs text-muted-foreground mb-1">{firstCategory.title}</div>
              )}
            <h2 className="text-xs md:text-sm font-medium leading-snug line-clamp-2 group-hover:text-primary text-foreground transition-colors duration-200">
              {titleToUse}
            </h2>
          </div>
        </article>
      </Link>
    )
  }

  // Feature variant (for featured posts with different styling)
  if (variant === 'feature') {
    return (
      <Link href={href} className="block group h-full">
        <article
          className={cn(
            'overflow-hidden rounded-lg border-none bg-card shadow-none hover:shadow-md group-hover:scale-102 transition-all duration-300 flex flex-col h-full',
            className,
          )}
        >
          {imageToDisplay && typeof imageToDisplay === 'object' && 'url' in imageToDisplay && (
            <div className="overflow-hidden flex-shrink-0">
              <Media
                resource={imageToDisplay as MediaType}
                priority={priority}
                className={cn('w-full h-full aspect-video', className)}
                imgClassName={cn('object-cover w-full h-full aspect-video', imgClassName)}
              />
            </div>
          )}
          <div className="px-4 py-5 flex-grow flex flex-col">
            {showCategories &&
              hasCategories &&
              firstCategory &&
              typeof firstCategory === 'object' && (
                <div className="text-xs text-muted-foreground mb-2">{firstCategory.title}</div>
              )}
            <h2 className="font-semibold text-base md:text-lg leading-snug line-clamp-2 group-hover:text-primary transition-colors duration-200">
              {titleToUse}
            </h2>
            {spot && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{spot}</p>}
            {doc && showDate && (
              <p className="text-xs text-slate-500 mt-auto pt-3">
                {new Date(
                  (doc.publishedAt as string) ||
                    (doc.createdAt as string) ||
                    new Date().toISOString(),
                ).toLocaleDateString('tr-TR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            )}
          </div>
        </article>
      </Link>
    )
  }

  // Default variant
  return (
    <Link href={href} className="block no-underline text-inherit group">
      <article
        className={cn(
          'border border-border rounded-lg overflow-hidden bg-card shadow-none hover:shadow-md group-hover:scale-102 transition-all duration-300',
          className,
        )}
      >
        <div className="relative w-full overflow-hidden">
          {!imageToDisplay && <div className="">No image</div>}
          {imageToDisplay && typeof imageToDisplay === 'object' && (
            <Media
              resource={imageToDisplay}
              size="33vw"
              className={cn('w-full h-full aspect-video', className)}
              imgClassName={cn('object-cover w-full h-full aspect-video', imgClassName)}
            />
          )}
        </div>
        <div className="px-4 py-5 flex flex-col">
          {showCategories &&
            hasCategories &&
            firstCategory &&
            typeof firstCategory === 'object' && (
              <div className="text-xs text-muted-foreground mb-2">{firstCategory.title}</div>
            )}
          {titleToUse && (
            <div className="prose">
              <h2 className="no-underline line-clamp-2 group-hover:text-primary transition-colors duration-200">
                {titleToUse}
              </h2>
            </div>
          )}
          {description && (
            <div className="mt-2 text-sm text-muted-foreground">
              {description && <p className="line-clamp-2">{sanitizedDescription}</p>}
            </div>
          )}
          {doc && showDate && (
            <p className="text-xs text-slate-500 mt-auto pt-3">
              {new Date(
                (doc.publishedAt as string) ||
                  (doc.createdAt as string) ||
                  new Date().toISOString(),
              ).toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          )}
        </div>
      </article>
    </Link>
  )
}
